import { formatNumber, NumberType } from '@x-swap-protocol/conedison/format'
import { ChainId } from '@xspswap/smart-order-router'
import { MAX_WIDTH_MEDIA_BREAKPOINT, SMALL_MEDIA_BREAKPOINT } from 'components/Tokens/constants'
import { LoadingBubble } from 'components/Tokens/loading'
import { MouseoverTooltip } from 'components/Tooltip'
import { CSSProperties, ForwardedRef, forwardRef, ReactNode } from 'react'
import { Info } from 'react-feather'
import styled, { css } from 'styled-components/macro'
import { shortenAddress } from 'utils'
import { ExplorerDataType, getExplorerLink } from 'utils/getExplorerLink'
import { isMobile } from 'utils/userAgent'

enum TokenSortMethod {
  SCORE = 'Score',
  VOLUME = 'Volume',
  SYMBOL = 'Symbol',
  NAME = 'Account',
}

const Cell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`
const StyledTokenRow = styled.div<{
  first?: boolean
  last?: boolean
  $loading?: boolean
}>`
  background-color: transparent;
  display: grid;
  font-size: 16px;
  grid-template-columns: 0.7fr 19fr 2.5fr 2.5fr;
  line-height: 24px;
  max-width: ${MAX_WIDTH_MEDIA_BREAKPOINT};
  ${({ first, last }) => css`
    height: ${first || last ? '72px' : '64px'};
    padding-top: ${first ? '8px' : '0px'};
    padding-bottom: ${last ? '8px' : '0px'};
  `}
  padding-left: 12px;
  padding-right: 12px;
  transition: ${({
    theme: {
      transition: { duration, timing },
    },
  }) => css`background-color ${duration.medium} ${timing.ease}`};
  width: 100%;
  transition-duration: ${({ theme }) => theme.transition.duration.fast};

  &:hover {
    ${({ $loading, theme }) =>
      !$loading &&
      css`
        background-color: ${theme.hoverDefault};
      `}
    ${({ last }) =>
      last &&
      css`
        border-radius: 0px 0px 8px 8px;
      `}
  }

  @media only screen and (max-width: ${SMALL_MEDIA_BREAKPOINT}) {
    grid-template-columns: 1fr 1fr;
  }
`

const ClickableContent = styled.div`
  display: flex;
  text-decoration: none;
  color: ${({ theme }) => theme.textPrimary};
  align-items: center;
  cursor: pointer;
`
const ClickableName = styled(ClickableContent)`
  gap: 8px;
  max-width: 100%;
`
const StyledHeaderRow = styled(StyledTokenRow)`
  border-bottom: 1px solid;
  border-color: ${({ theme }) => theme.backgroundOutline};
  border-radius: 8px 8px 0px 0px;
  color: ${({ theme }) => theme.textTertiary};
  font-size: 14px;
  height: 48px;
  line-height: 16px;
  padding: 0px 12px;
  width: 100%;
  justify-content: center;

  &:hover {
    background-color: transparent;
  }

  @media only screen and (max-width: ${SMALL_MEDIA_BREAKPOINT}) {
    justify-content: space-between;
  }
`

const ListNumberCell = styled(Cell)<{ header: boolean }>`
  color: ${({ theme }) => theme.textTertiary};
  min-width: 32px;
  font-size: 14px;

  @media only screen and (max-width: ${SMALL_MEDIA_BREAKPOINT}) {
    display: none;
  }
`
const DataCell = styled(Cell)<{ sortable: boolean }>`
  justify-content: flex-end;
  min-width: 80px;
  user-select: ${({ sortable }) => (sortable ? 'none' : 'unset')};
  transition: ${({
    theme: {
      transition: { duration, timing },
    },
  }) => css`background-color ${duration.medium} ${timing.ease}`};
`

const NameCell = styled(Cell)`
  justify-content: flex-start;
  padding: 0px 8px;
  min-width: 190px;
  gap: 8px;
  @media only screen and (max-width: ${SMALL_MEDIA_BREAKPOINT}) {
    width: 130px;
    min-width: 130px;
  }
`

const PriceCell = styled(DataCell)`
  padding-right: 8px;
  justify-content: flex-end;
  @media only screen and (max-width: ${SMALL_MEDIA_BREAKPOINT}) {
    width: 90px;
  }
`

const TvlCell = styled(DataCell)`
  padding-right: 8px;
  justify-content: center;
  @media only screen and (max-width: ${SMALL_MEDIA_BREAKPOINT}) {
    display: none;
  }
`

const HeaderCellWrapper = styled.span<{ textAlign?: 'left' | 'right' | 'center' }>`
  align-items: center;
  display: flex;
  gap: 4px;
  justify-content: ${({ textAlign }) =>
    textAlign === 'left' ? 'flex-start' : textAlign === 'right' ? 'flex-end' : 'center'};
  width: 100%;
`
// const SparkLineCell = styled(Cell)`
//   padding: 0px 24px;
//   min-width: 120px;

//   @media only screen and (max-width: ${MAX_WIDTH_MEDIA_BREAKPOINT}) {
//     display: none;
//   }
// `
// const SparkLine = styled(Cell)`
//   width: 124px;
//   height: 42px;
// `
const StyledLink = styled.a`
  text-decoration: none;
`

const TokenName = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
`

const SmallLoadingBubble = styled(LoadingBubble)`
  width: 25%;
`
const MediumLoadingBubble = styled(LoadingBubble)`
  width: 65%;
`
const LongLoadingBubble = styled(LoadingBubble)`
  width: 90%;
`
const IconLoadingBubble = styled(LoadingBubble)`
  border-radius: 50%;
  width: 24px;
`
const SparkLineLoadingBubble = styled(LongLoadingBubble)`
  height: 4px;
`

const InfoIconContainer = styled.div`
  margin-left: 2px;
  display: flex;
  align-items: center;
  cursor: help;
`

const HEADER_DESCRIPTIONS: Record<TokenSortMethod, ReactNode | undefined> = {
  [TokenSortMethod.SCORE]: undefined,
  [TokenSortMethod.SYMBOL]: undefined,
  [TokenSortMethod.NAME]: undefined,
  [TokenSortMethod.VOLUME]: undefined,
}

/* Get singular header cell for header row */
function HeaderCell({
  category,
  textAlign = 'right',
}: {
  category: TokenSortMethod // TODO: change this to make it work for trans
  textAlign?: 'left' | 'right' | 'center'
}) {
  const description = HEADER_DESCRIPTIONS[category]

  return (
    <HeaderCellWrapper textAlign={textAlign}>
      {category}
      {description && (
        <MouseoverTooltip text={description} placement="right">
          <InfoIconContainer>
            <Info size={14} />
          </InfoIconContainer>
        </MouseoverTooltip>
      )}
    </HeaderCellWrapper>
  )
}

/* Token Row: skeleton row component */
function TokenRow({
  header,
  listNumber,
  tokenInfo,
  price,
  tvl,
  ...rest
}: {
  first?: boolean
  header: boolean
  listNumber?: ReactNode
  loading?: boolean
  price: ReactNode
  tokenInfo: ReactNode
  last?: boolean
  style?: CSSProperties
  tvl: ReactNode
}) {
  const rowCells = (
    <>
      <ListNumberCell header={header}>{listNumber}</ListNumberCell>
      <NameCell data-testid="name-cell">{tokenInfo}</NameCell>
      <TvlCell data-testid="tvl-cell" sortable={header}>
        {tvl}
      </TvlCell>
      <PriceCell data-testid="price-cell" sortable={header}>
        {price}
      </PriceCell>
    </>
  )
  if (header) return <StyledHeaderRow data-testid="header-row">{rowCells}</StyledHeaderRow>
  return <StyledTokenRow {...rest}>{rowCells}</StyledTokenRow>
}

/* Header Row: top header row component for table */
export function TopTradersHeaderRow() {
  return (
    <TokenRow
      header={true}
      listNumber="#"
      tokenInfo={<HeaderCell category={TokenSortMethod.NAME} textAlign="left" />}
      price={<HeaderCell category={TokenSortMethod.SCORE} textAlign="right" />}
      tvl={<HeaderCell category={TokenSortMethod.VOLUME} textAlign="center" />}
    />
  )
}

/* Loading State: row component with loading bubbles */
export function TopTradersLoadingRow(props: { first?: boolean; last?: boolean }) {
  return (
    <TokenRow
      header={false}
      listNumber={<SmallLoadingBubble />}
      tokenInfo={
        <>
          <MediumLoadingBubble />
        </>
      }
      price={<MediumLoadingBubble />}
      tvl={<LoadingBubble />}
      {...props}
    />
  )
}

interface LoadedRowProps {
  tokenListIndex: number
  tokenListLength: number
  // sparklineMap: SparklineMap
  sortRank: number
  data: {
    id: string
    totalVolume: string
    clearVolume: string
  }
}

/* Loaded State: row component with token information */
export const TopTradersRow = forwardRef(
  (
    { tokenListIndex, tokenListLength, data: { id: account, totalVolume, clearVolume }, sortRank }: LoadedRowProps,
    ref: ForwardedRef<HTMLDivElement>
  ) => {
    return (
      <div ref={ref} data-testid={`token-table-row-${account}`}>
        <StyledLink target="_blank" href={getExplorerLink(ChainId.XDC, account, ExplorerDataType.ADDRESS)}>
          <TokenRow
            header={false}
            listNumber={sortRank}
            tokenInfo={
              <ClickableName>
                <TokenName data-cy="token-name">{isMobile ? shortenAddress(account) : account}</TokenName>
              </ClickableName>
            }
            price={<ClickableContent>{`${formatNumber(parseFloat(totalVolume))}`}</ClickableContent>}
            tvl={
              <ClickableContent>
                {`${formatNumber(parseFloat(clearVolume), NumberType.FiatTokenStats)}`}
              </ClickableContent>
            }
            first={tokenListIndex === 0}
            last={tokenListIndex === tokenListLength - 1}
          />
        </StyledLink>
      </div>
    )
  }
)

TopTradersRow.displayName = 'TopTradersRow'
